import { useTranslate } from "@refinedev/core";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { formatSecondsAsMMSS } from "helpers/utils";
import { ViewerActivityEvent } from "types";
import { TimelineItemTone } from "@libertify/ui";

// formatSecondsAsMMSS relies on dayjs.duration(), which some call sites
// extend globally as a side effect of importing an unrelated module — extend
// it here too so this util doesn't depend on load order.
dayjs.extend(duration);

const T_PREFIX = "media.projects.analytics.activity";

function getReaderStageLabel(
  stage: string | null | undefined,
  t: ReturnType<typeof useTranslate>
): string {
  switch (stage) {
    case "deep_readers":
      return t(`${T_PREFIX}.readerStage.deepReaders`);
    case "completers":
      return t(`${T_PREFIX}.readerStage.completers`);
    default:
      // Defensive: reader_stage is typed as a plain string (not a literal
      // union) on the generated schema, so unknown values fall back to the
      // raw value rather than crashing on an exhaustive switch.
      return stage ?? "";
  }
}

export function getActivityEventDescription(
  event: ViewerActivityEvent,
  t: ReturnType<typeof useTranslate>
): string {
  const position = formatSecondsAsMMSS(event.video_position_seconds ?? 0);

  switch (event.event_kind) {
    case "opened":
      return t(`${T_PREFIX}.events.opened`);
    case "video_started":
      return t(`${T_PREFIX}.events.videoStarted`);
    case "video_paused":
      return t(`${T_PREFIX}.events.videoPaused`, { position });
    case "video_rewound":
      return t(`${T_PREFIX}.events.videoRewound`, { position });
    case "video_skipped_ahead":
      return t(`${T_PREFIX}.events.videoSkippedAhead`, { position });
    case "video_completed":
      return t(`${T_PREFIX}.events.videoCompleted`);
    case "playback_speed_changed":
      return t(`${T_PREFIX}.events.playbackSpeedChanged`, {
        rate: event.playback_rate ?? 1,
      });
    case "chapter_jumped":
      return t(`${T_PREFIX}.events.chapterJumped`, {
        chapter: (event.chapter_index ?? 0) + 1,
      });
    case "page_reached": {
      const depth = event.read_depth_pct ?? 0;
      return event.page_number != null
        ? t(`${T_PREFIX}.events.pageReached`, {
            page: event.page_number,
            depth,
          })
        : t(`${T_PREFIX}.events.pageReachedNoPage`, { depth });
    }
    case "backtracked":
      return t(`${T_PREFIX}.events.backtracked`, {
        fromPage: event.from_page ?? "—",
        toPage: event.to_page ?? "—",
      });
    case "reader_stage_reached":
      return t(`${T_PREFIX}.events.readerStageReached`, {
        stage: getReaderStageLabel(event.reader_stage, t),
        page: event.page_number ?? "—",
      });
    case "chat_opened":
      return t(`${T_PREFIX}.events.chatOpened`);
    case "message_sent":
      return t(`${T_PREFIX}.events.messageSent`, {
        message: event.message_text ?? "",
      });
    case "source_clicked":
      return t(`${T_PREFIX}.events.sourceClicked`);
    case "transcript_exported":
      return t(`${T_PREFIX}.events.transcriptExported`);
    case "dropped_off":
      return event.page_number != null
        ? t(`${T_PREFIX}.events.droppedOff`, { page: event.page_number })
        : t(`${T_PREFIX}.events.droppedOffNoPage`);
    default:
      return t(`${T_PREFIX}.events.unknown`);
  }
}

// Tone-per-kind mapping is a design call (flagged in the PR for confirmation):
// dropped_off reads as a warning, completion/stage milestones as success,
// chat/message events as accent, everything else neutral.

export function getActivityEventTone(
  event: Pick<ViewerActivityEvent, "event_kind">
): TimelineItemTone {
  switch (event.event_kind) {
    case "opened":
    case "chat_opened":
    case "video_completed":
      return "green";
    case "dropped_off":
      return "red";
    case "video_started":
    case "video_rewound":
    case "video_paused":
    case "chapter_jumped":
    case "video_skipped_ahead":
      return "orange";
    case "page_reached":
    case "backtracked":
    case "reader_stage_reached":
    case "transcript_exported":
      return "blue";
    case "message_sent":
    case "source_clicked":
    case "playback_speed_changed":
      return "purple";
    default:
      return "neutral";
  }
}
